/**
 * Rejestr narzędzi — jedno miejsce, w którym spotykają się wszystkie nazwy tej samej rzeczy.
 *
 * Powód istnienia: dziś to samo narzędzie nazywa się różnie w czterech miejscach —
 * slug we froncie (`zaznacz-dzialke`), `VariantType` w `processing_jobs` (`plot_marked`),
 * klucz rozliczeniowy w `TOOL_COSTS` (`plot_visualization`) i klucz uprawnień w
 * `subscription_plans.allowed_tool_keys`. Wszystkie cztery to stringi przechodzące przez
 * granicę frameworka, więc żaden rozjazd nie zapali się w typechecku ani w teście
 * jednostkowym — dopiero na produkcji, jako „narzędzie nie działa mimo opłaconego planu".
 *
 * `ToolKey` jest kluczem uprawnień i to on trafia do bazy. Reszta identyfikatorów wisi
 * przy nim tutaj, żeby dało się je przetłumaczyć w jednym kierunku i tylko raz.
 *
 * Pułapka, którą ten rejestr utrwala: `wizualizacja` ROZLICZA się jako
 * `floor_plan_visualization`, ale zapisuje joby jako `arranged` (nazwa historyczna z v1).
 * Porównywanie tych dwóch pól ze sobą da fałszywy alarm — patrz `billingKey` vs `dbVariant`.
 *
 * Plan: ai/plans/2026-08-11-migracja-na-subskrypcje.md (§3.4)
 */
import type { VariantType } from "../types/job.types";

/** Klucz uprawnień — wartość zapisywana w subscription_plans.allowed_tool_keys / metered_tools. */
export type ToolKey =
  | "virtual_staging"
  | "decluttering"
  | "room_cleanup"
  | "empty_room_arrangement"
  | "floor_plan_visualization"
  | "plot_visualization"
  | "team_photo"
  | "plot_marking"
  | "rcn_valuation"
  | "kw_lookup";

/**
 * Jak narzędzie jest rozliczane:
 * - `credits` — każde użycie kosztuje kredyty (`creditCost`)
 * - `metered` — kredytów nie zdejmuje, ale ma limit ilościowy z planu (`feature_usage`)
 */
export type ToolBilling = "credits" | "metered";

export interface ToolDefinition {
  key: ToolKey;
  /** Etykieta PL — do komunikatów błędów po stronie API (front ma własne teksty). */
  label: string;
  billing: ToolBilling;
  /** Koszt w kredytach; 0 dla narzędzi `metered`. Dla rzutu 3D — koszt JEDNEGO etapu. */
  creditCost: number;
  /** Slug w apps/web/src/lib/data.ts (TOOLS). `null` = narzędzie bez własnego kafelka. */
  slug: string | null;
  /** Wartość zapisywana w processing_jobs.variant_type. `null` = nie tworzy joba. */
  dbVariant: VariantType | null;
  /** Klucz w TOOL_COSTS. Zwykle == dbVariant, ale NIE dla rzutu 3D (patrz nagłówek). */
  billingKey: VariantType | null;
}

export const TOOL_REGISTRY: Readonly<Record<ToolKey, ToolDefinition>> = {
  virtual_staging: {
    key: "virtual_staging",
    label: "Home Staging",
    billing: "credits",
    creditCost: 3,
    slug: "home-staging",
    dbVariant: "virtual_staging",
    billingKey: "virtual_staging",
  },
  decluttering: {
    key: "decluttering",
    label: "Pusty pokój",
    billing: "credits",
    creditCost: 1,
    slug: "czysty-pokoj",
    dbVariant: "decluttering",
    billingKey: "decluttering",
  },
  room_cleanup: {
    key: "room_cleanup",
    label: "Sprzątanie i retusz",
    billing: "credits",
    creditCost: 3,
    slug: "sprzatanie",
    dbVariant: "room_cleanup",
    billingKey: "room_cleanup",
  },
  empty_room_arrangement: {
    key: "empty_room_arrangement",
    label: "Wirtualny remont",
    billing: "credits",
    creditCost: 3,
    slug: "remont",
    dbVariant: "empty_room_arrangement",
    billingKey: "empty_room_arrangement",
  },
  floor_plan_visualization: {
    key: "floor_plan_visualization",
    label: "Rzut 3D",
    billing: "credits",
    creditCost: 2, // per etap; pełna paczka trzech etapów = 6 kr
    slug: "wizualizacja",
    dbVariant: "arranged", // ⚠️ historyczna nazwa w DB — NIE równa się billingKey
    billingKey: "floor_plan_visualization",
  },
  plot_visualization: {
    key: "plot_visualization",
    label: "Zabudowa działki",
    billing: "credits",
    creditCost: 5,
    slug: "zabudowa",
    dbVariant: "plot_visualization",
    billingKey: "plot_visualization",
  },
  team_photo: {
    key: "team_photo",
    label: "Zdjęcia biznesowe",
    billing: "credits",
    creditCost: 5,
    slug: "zespol",
    dbVariant: "team_photo",
    billingKey: "team_photo",
  },
  plot_marking: {
    key: "plot_marking",
    label: "Zaznacz działkę",
    billing: "metered",
    creditCost: 0,
    slug: "zaznacz-dzialke",
    // Świadomie null, mimo że narzędzie ZAPISUJE joby. CHECK na
    // processing_jobs.variant_type (baza v1) nie zna wartości „plot_marked", więc
    // zaznaczone działki lądują jako `plot_visualization` i rozpoznaje się je po
    // input_image = "plot-marked-input" (patrz plot-step.routes.ts, akcja save-marked).
    // Mapowanie variant → ToolKey musiałoby tu skłamać, więc go nie ma.
    dbVariant: null,
    billingKey: null,
  },
  rcn_valuation: {
    key: "rcn_valuation",
    label: "Wycena RCN",
    billing: "metered",
    creditCost: 0,
    slug: "wycena",
    dbVariant: null,
    billingKey: null,
  },
  kw_lookup: {
    key: "kw_lookup",
    label: "Sprawdzenie księgi wieczystej",
    billing: "metered",
    creditCost: 0,
    // Było `null` z uzasadnieniem „wywoływane z formularza oferty, nie z kafelka
    // w Studiu". Od migracji 035 KW ma własny kafelek i własną zakładkę, więc slug
    // musi istnieć — bez niego `toolKeyBySlug` zwraca null i front nie potrafi
    // powiązać ekranu z uprawnieniem ani z limitem planu.
    slug: "ksiegi-wieczyste",
    dbVariant: null,
    billingKey: null,
  },
};

export const TOOL_KEYS = Object.keys(TOOL_REGISTRY) as ToolKey[];

export function isToolKey(value: string): value is ToolKey {
  return Object.prototype.hasOwnProperty.call(TOOL_REGISTRY, value);
}

export function getTool(key: ToolKey): ToolDefinition {
  return TOOL_REGISTRY[key];
}

/** Slug frontu → klucz uprawnień. Zwraca `null` dla nieznanego sluga (nie rzuca). */
export function toolKeyBySlug(slug: string): ToolKey | null {
  return TOOL_KEYS.find((key) => TOOL_REGISTRY[key].slug === slug) ?? null;
}

/**
 * `processing_jobs.variant_type` → klucz uprawnień.
 *
 * Etapy rzutu 3D (`fp_2d`, `fp_3d_top`, `fp_3d_iso`) mapują się na `floor_plan_visualization`,
 * bo uprawnienie dotyczy narzędzia, a nie pojedynczego etapu pipeline'u.
 */
export function toolKeyByVariant(variant: VariantType): ToolKey | null {
  // `floor_plan_visualization` jest jednocześnie ToolKey i legalnym VariantType
  // (tak zapisuje się `tool_type` rzutu 3D), ale w rejestrze ma dbVariant `arranged`,
  // więc wyszukiwanie po dbVariant go NIE znajdzie. Bez tej gałęzi bramka dostaje
  // null i przepuszcza wywołanie bez sprawdzenia planu.
  if (
    variant === "floor_plan_visualization" ||
    variant === "fp_2d" ||
    variant === "fp_3d_top" ||
    variant === "fp_3d_iso"
  ) {
    return "floor_plan_visualization";
  }
  return TOOL_KEYS.find((key) => TOOL_REGISTRY[key].dbVariant === variant) ?? null;
}

/** Narzędzia limitowane licznikiem, nie kredytami — te, które mają wpis w `metered_tools`. */
export const METERED_TOOL_KEYS: ToolKey[] = TOOL_KEYS.filter(
  (key) => TOOL_REGISTRY[key].billing === "metered",
);
